/**
 * Opening and saving documents. Ported from the desktop's `main/ipc/files.ts`:
 * every write goes through here, so the data-integrity rules hold in one place.
 *
 *  - A save refuses to replace a file another program changed since it was
 *    read, and the page asks before overwriting.
 *  - The file's bytes are kept as a version before a save replaces them.
 *  - Encoding, BOM and line endings are written back as they were read.
 *  - Writes are atomic: `createWritable` writes to a swap file the browser
 *    moves into place on close, so a crash mid-save leaves the old file whole.
 *
 * Where the browser has no File System Access, a document can be read but not
 * written in place, and saving downloads it instead.
 */
import type { DocumentFile, SaveRequest, SaveResult, TextFile } from './types'
import { decodeText, encodeText } from './textfile'
import {
  fileFor,
  fileHandleFor,
  getOpenFolder,
  hasFileSystemAccess,
  handleRecord,
  PlatformError,
  pathForFileHandle,
  readCopy,
  storeCopy,
  toPlatformError,
  updateCopy,
  directoryFor,
} from './handles'
import { baseName, formatPath, parsePath } from './paths'
import { backup, clearJournal } from './recovery'
import { getSettings, patchSettings } from './settings'
import { track } from './watch'
import { randomId } from './db'

/** What the pickers offer, as the desktop's dialogs did. */
export const MARKDOWN_TYPES: FilePickerAcceptType[] = [
  {
    description: 'Markdown',
    accept: { 'text/markdown': ['.md', '.markdown', '.mdown', '.mkd'], 'text/plain': ['.txt'] },
  },
]
const ACCEPT = '.md,.markdown,.mdown,.mkd,.txt,text/markdown,text/plain'

/** Keeps the most recent 15 paths, most recent first, without duplicates. */
export function addRecentFile(path: string): void {
  const p = parsePath(path)
  // A download has nothing to reopen; a snapshot folder's file is gone with its page.
  if (!p || p.scheme === 'download' || p.scheme === 'snap') return
  const lower = path.toLowerCase()
  const recent = [path, ...getSettings().recentFiles.filter((r) => r.toLowerCase() !== lower)]
  patchSettings({ recentFiles: recent.slice(0, 15) })
}

function documentFrom(path: string, text: TextFile, mtimeMs: number): DocumentFile {
  return { path, ...text, mtimeMs }
}

/** Reads a document by its path. */
export async function readDocument(
  path: string,
  opts: { recent?: boolean } = {}
): Promise<DocumentFile> {
  const p = parsePath(path)
  if (!p) throw new PlatformError('ENOENT', `${path} cannot be opened in a browser.`)
  if (p.scheme === 'copy') {
    const rec = await readCopy(p.id)
    if (!rec) throw new PlatformError('ENOENT', `${baseName(path)} is no longer kept.`)
    await updateCopy(p.id, {})
    if (opts.recent !== false) addRecentFile(path)
    return {
      path,
      content: rec.content,
      encoding: rec.encoding,
      hasBom: rec.hasBom,
      eol: rec.eol,
      mtimeMs: rec.mtimeMs,
    }
  }
  if (p.scheme === 'download') {
    throw new PlatformError('ENOENT', `${baseName(path)} was only ever downloaded.`)
  }
  const file = await fileFor(path)
  const bytes = new Uint8Array(await file.arrayBuffer())
  track(path, file.lastModified)
  if (opts.recent !== false) addRecentFile(path)
  return documentFrom(path, decodeText(bytes), file.lastModified)
}

/** Hands bytes to the browser's downloads, under a name. */
export function download(name: string, data: Uint8Array | string, type: string): void {
  const blob = new Blob([data as BlobPart], { type })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoked after the browser has taken it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/** Saves a document to its path. Never throws: a failure comes back as a result. */
export async function saveDocument(req: SaveRequest): Promise<SaveResult> {
  const p = parsePath(req.path)
  const bytes = encodeText(req.content, req)
  try {
    if (!p) throw new PlatformError('EROFS', `${req.path} cannot be written from a browser.`)

    if (p.scheme === 'copy' || p.scheme === 'download') {
      download(baseName(req.path), bytes, 'text/markdown;charset=utf-8')
      const now = Date.now()
      if (p.scheme === 'copy') {
        await updateCopy(p.id, {
          content: req.content,
          encoding: req.encoding,
          hasBom: req.hasBom,
          eol: req.eol,
          mtimeMs: now,
          size: bytes.byteLength,
        })
        addRecentFile(req.path)
      }
      await clearJournal(req.path)
      return { ok: true, mtimeMs: now }
    }

    if (p.scheme === 'snap') {
      throw new PlatformError(
        'EROFS',
        'this folder was opened read-only, because this browser cannot write to files.'
      )
    }

    const handle = await fileHandleFor(req.path, 'readwrite', { create: true })
    let previous: Uint8Array | null = null
    try {
      const file = await handle.getFile()
      // Any other time, not only a newer one: a sync client or a backup
      // restore puts a file back with its original, older time.
      if (
        req.expectedMtimeMs !== undefined &&
        Math.abs(file.lastModified - req.expectedMtimeMs) > 1
      ) {
        return {
          ok: false,
          reason: 'conflict',
          message: 'The file changed on disk since it was opened.',
        }
      }
      previous = new Uint8Array(await file.arrayBuffer())
    } catch (err) {
      if ((err as DOMException)?.name !== 'NotFoundError') throw err
    }

    if (previous && previous.byteLength > 0) await backup(req.path, previous)
    const writable = await handle.createWritable()
    try {
      await writable.write(bytes as BufferSource)
      await writable.close()
    } catch (err) {
      await writable.abort().catch(() => {})
      throw err
    }
    await clearJournal(req.path)
    const written = await handle.getFile()
    track(req.path, written.lastModified)
    addRecentFile(req.path)
    return { ok: true, mtimeMs: written.lastModified }
  } catch (err) {
    const e = toPlatformError(err, baseName(req.path))
    const code = e instanceof PlatformError ? e.code : undefined
    return { ok: false, reason: 'error', message: e.message.replace(/^E[A-Z]+: /, ''), code }
  }
}

/**
 * Asks where to save. With File System Access that is the browser's own save
 * dialog, starting in the open folder; without it there is no dialog to show,
 * and the document will be downloaded under the suggested name.
 */
export async function saveAsDialog(suggested?: string): Promise<string | null> {
  const name = baseName(suggested ?? 'Untitled.md') || 'Untitled.md'
  if (!hasFileSystemAccess) return formatPath('download', randomId(), [name])

  let startIn: FileSystemHandle | undefined
  try {
    const folder = getOpenFolder()
    if (folder && parsePath(folder)?.scheme === 'ws')
      startIn = await directoryFor(folder, 'read', { ask: false })
    else if (suggested && parsePath(suggested)?.scheme === 'fh') {
      startIn = (await handleRecord(parsePath(suggested)!.id))?.handle
    }
  } catch {
    startIn = undefined
  }
  try {
    const handle = await window.showSaveFilePicker({
      suggestedName: name,
      types: MARKDOWN_TYPES,
      ...(startIn ? { startIn } : {}),
    })
    return await pathForFileHandle(handle)
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return null
    throw toPlatformError(err, name)
  }
}

/** Reads files the user chose without File System Access: kept as copies. */
async function adoptFiles(files: File[]): Promise<DocumentFile[]> {
  const out: DocumentFile[] = []
  for (const file of files) {
    const bytes = new Uint8Array(await file.arrayBuffer())
    const text = decodeText(bytes)
    const path = await storeCopy({
      name: file.name,
      ...text,
      mtimeMs: file.lastModified,
      size: file.size,
    })
    addRecentFile(path)
    out.push(documentFrom(path, text, file.lastModified))
  }
  return out
}

/** Opens files of their own as copies (drop or input without a handle). */
export function openCopies(files: File[]): Promise<DocumentFile[]> {
  return adoptFiles(files)
}

/** Shows an <input type=file> and resolves with what was chosen, or null. */
export function pickWithInput(opts: {
  directory?: boolean
  multiple?: boolean
  accept?: string
}): Promise<File[] | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    if (opts.accept) input.accept = opts.accept
    input.multiple = opts.multiple ?? false
    if (opts.directory) input.webkitdirectory = true
    input.style.display = 'none'
    let settled = false
    const done = (files: File[] | null): void => {
      if (settled) return
      settled = true
      input.remove()
      resolve(files)
    }
    input.addEventListener('change', () => done(input.files ? [...input.files] : null))
    input.addEventListener('cancel', () => done(null))
    document.body.appendChild(input)
    input.click()
  })
}

/** File ▸ Open. */
export async function openDialog(): Promise<DocumentFile[] | null> {
  if (hasFileSystemAccess) {
    let handles: FileSystemFileHandle[]
    try {
      handles = await window.showOpenFilePicker({ multiple: true, types: MARKDOWN_TYPES })
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return null
      throw toPlatformError(err, 'the file')
    }
    const out: DocumentFile[] = []
    for (const h of handles) out.push(await readDocument(await pathForFileHandle(h)))
    return out
  }
  const files = await pickWithInput({ multiple: true, accept: ACCEPT })
  if (!files || files.length === 0) return null
  return adoptFiles(files)
}
